import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, NgZone, PLATFORM_ID } from '@angular/core';
import { environment } from '../../environments/environment';

declare global {
  interface Window {
    grecaptcha?: {
      ready: (callback: () => void) => void;
      execute: (
        siteKey: string,
        options: { action: string },
      ) => Promise<string>;
    };
  }
}

function maskSiteKey(siteKey: string): string {
  if (!siteKey) {
    return '(empty)';
  }
  if (siteKey.length <= 12) {
    return '***';
  }
  return `${siteKey.slice(0, 6)}...${siteKey.slice(-4)}`;
}

@Injectable({
  providedIn: 'root',
})
export class RecaptchaService {
  private scriptLoading?: Promise<void>;
  private diagnosticsLogged = false;

  constructor(
    private ngZone: NgZone,
    @Inject(PLATFORM_ID) private platformId: object,
  ) {}

  isConfigured(): boolean {
    return Boolean(environment.recaptchaSiteKey);
  }

  /** Start loading the reCAPTCHA script before the user submits. */
  warmup(): void {
    if (!isPlatformBrowser(this.platformId) || !this.isConfigured()) {
      return;
    }

    void this.ensureScriptLoaded(environment.recaptchaSiteKey).catch((error) => {
      this.logWarn('warmup failed', {
        error: error instanceof Error ? error.message : String(error),
      });
    });
  }

  logConfiguration(): void {
    if (this.diagnosticsLogged || typeof window === 'undefined') {
      return;
    }

    this.diagnosticsLogged = true;
    const hostname = window.location.hostname;
    const configured = this.isConfigured();

    this.logInfo('configuration', {
      configured,
      hostname,
      siteKey: maskSiteKey(environment.recaptchaSiteKey),
      production: environment.production,
      apiBase: environment.apiBase,
    });

    if (!configured) {
      this.logWarn('Formularze kontakt/współpraca wyłączone — brak recaptchaSiteKey w buildzie');
      return;
    }

    this.logInfo(
      'Upewnij się, że hostname jest na liście Domains w Google reCAPTCHA Admin (v3)',
      {
        hostname,
        requiredDomains: [
          'gaponline.pl',
          'www.gaponline.pl',
          'test.gaponline.pl',
          'localhost',
        ],
      },
    );
  }

  async execute(action: string): Promise<string> {
    this.logConfiguration();

    const siteKey = environment.recaptchaSiteKey;
    if (!siteKey) {
      return '';
    }

    const hostname =
      typeof window !== 'undefined' ? window.location.hostname : '';

    this.logInfo('execute start', {
      action,
      hostname,
      siteKey: maskSiteKey(siteKey),
    });

    try {
      await this.ensureScriptLoaded(siteKey);
      await this.whenGrecaptchaReady();
      const token = await this.executeRecaptcha(siteKey, action);

      this.logInfo('execute success', {
        action,
        hostname,
        tokenLength: token.length,
      });

      return token;
    } catch (error) {
      this.logExecuteFailure(action, hostname, error);
      throw error;
    }
  }

  private executeRecaptcha(siteKey: string, action: string): Promise<string> {
    return new Promise((resolve, reject) => {
      const grecaptcha = window.grecaptcha;
      if (!grecaptcha?.execute) {
        reject(new Error('grecaptcha.execute is not available after ready()'));
        return;
      }

      grecaptcha
        .execute(siteKey, { action })
        .then((token) => {
          this.ngZone.run(() => {
            if (!token?.trim()) {
              reject(new Error('reCAPTCHA returned an empty token'));
              return;
            }
            resolve(token);
          });
        })
        .catch((error) => {
          this.ngZone.run(() => reject(error));
        });
    });
  }

  /**
   * api.js onload is not enough: grecaptcha.ready exists first, execute() is
   * attached later. Always wait for ready() before the first execute().
   */
  private whenGrecaptchaReady(): Promise<void> {
    return new Promise((resolve, reject) => {
      let settled = false;

      const timeoutId = window.setTimeout(() => {
        if (settled) {
          return;
        }
        settled = true;
        this.ngZone.run(() =>
          reject(new Error('reCAPTCHA timed out while waiting to become ready')),
        );
      }, 15_000);

      const succeed = () => {
        if (settled) {
          return;
        }
        settled = true;
        window.clearTimeout(timeoutId);
        this.ngZone.run(() => resolve());
      };

      const waitForExecute = () => {
        if (settled) {
          return;
        }
        if (window.grecaptcha?.execute) {
          succeed();
          return;
        }
        window.setTimeout(waitForExecute, 50);
      };

      const waitForReadyFn = () => {
        if (settled) {
          return;
        }
        if (window.grecaptcha?.ready) {
          window.grecaptcha.ready(waitForExecute);
          return;
        }
        window.setTimeout(waitForReadyFn, 50);
      };

      waitForReadyFn();
    });
  }

  private logExecuteFailure(
    action: string,
    hostname: string,
    error: unknown,
  ): void {
    const message = error instanceof Error ? error.message : String(error);

    this.logWarn('execute failed', {
      action,
      hostname,
      siteKey: maskSiteKey(environment.recaptchaSiteKey),
      error: message,
    });

    this.logWarn(
      'Typowy błąd „Invalid domain for site key” → dodaj bieżącą domenę w reCAPTCHA Admin → Settings → Domains',
      {
        hostname,
        addDomain: hostname,
        adminUrl: 'https://www.google.com/recaptcha/admin',
      },
    );
  }

  private ensureScriptLoaded(siteKey: string): Promise<void> {
    if (typeof window === 'undefined') {
      return Promise.reject(new Error('reCAPTCHA requires a browser window'));
    }

    if (window.grecaptcha?.ready) {
      return Promise.resolve();
    }

    if (!this.scriptLoading) {
      this.scriptLoading = this.insertOrWaitForScript(siteKey).catch((error) => {
        this.scriptLoading = undefined;
        throw error;
      });
    }

    return this.scriptLoading;
  }

  private insertOrWaitForScript(siteKey: string): Promise<void> {
    const scriptUrl = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`;

    return new Promise((resolve, reject) => {
      const existing = document.querySelector<HTMLScriptElement>(
        'script[data-gap-recaptcha="true"]',
      );

      if (existing) {
        resolve();
        return;
      }

      this.logInfo('loading script', {
        siteKey: maskSiteKey(siteKey),
        scriptUrl: scriptUrl.replace(siteKey, maskSiteKey(siteKey)),
      });

      const script = document.createElement('script');
      script.src = scriptUrl;
      script.async = true;
      script.dataset.gapRecaptcha = 'true';
      script.onload = () => {
        this.logInfo('script loaded');
        resolve();
      };
      script.onerror = () => {
        this.logWarn('script failed to load', {
          scriptUrl: scriptUrl.replace(siteKey, maskSiteKey(siteKey)),
        });
        reject(new Error('reCAPTCHA script failed to load'));
      };
      document.head.appendChild(script);
    });
  }

  private logInfo(message: string, details?: Record<string, unknown>): void {
    if (environment.production) {
      return;
    }
    console.info('[GAP reCAPTCHA]', message, details ?? '');
  }

  private logWarn(message: string, details?: Record<string, unknown>): void {
    console.warn('[GAP reCAPTCHA]', message, details ?? '');
  }
}
